import React, { useRef, useState, useEffect, useCallback } from "react";
import { Tooltip, TooltipPlacement } from "src/assets/imports/antdComponents";

interface IEllipsisTextProps {
	text: React.ReactNode;
	children?: React.ReactNode;
	maxLines?: number;
	lineHeight?: number;
	className?: string;
	width?: number;
	maxWidth?: number;
	tooltipPlacement?: TooltipPlacement;
	fontSize?: string | number;
	getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
	showTooltip?: boolean;
}

export const EllipsisText: React.FC<IEllipsisTextProps> = ({
	text,
	children,
	maxLines = 2,
	lineHeight = 16,
	className = "",
	width,
	maxWidth,
	tooltipPlacement = "top",
	showTooltip = true,
	fontSize,
	getPopupContainer,
}) => {
	const textRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);
	const [isReady, setIsReady] = useState(false);

	// Re-run truncation check when fontSize or maxLines changes.
	// Both affect rendered height, so stale truncation state must be cleared.
	const checkTruncation = useCallback(() => {
		const element = textRef.current;
		if (element && element.offsetWidth > 0) {
			setIsReady(true);
			setIsTruncated(element.scrollHeight > element.clientHeight);
		}
	}, [fontSize, maxLines]); // ← fontSize + maxLines added as deps

	useEffect(() => {
		const timer = setTimeout(checkTruncation, 0);
		window.addEventListener("resize", checkTruncation);
		if (document.fonts) {
			document.fonts.ready.then(checkTruncation);
		}
		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", checkTruncation);
		};
	}, [text, checkTruncation]);

	const displayContent = children ?? text;

	const textElement = (
		<span
			ref={textRef}
			className={`w-full block ${className}`}
			style={{
				display: "-webkit-box",
				WebkitLineClamp: maxLines,
				WebkitBoxOrient: "vertical",
				overflow: "hidden",
				width: width ? `${width}px` : "auto",
				...(maxWidth !== undefined ? { maxWidth: `${maxWidth}px` } : {}),
				textOverflow: "ellipsis",
				lineHeight: `${lineHeight}px`,
				wordBreak: "break-word",
				minWidth: 0,
				visibility: isReady ? "visible" : "hidden",
				...(fontSize !== undefined ? { fontSize } : {}),
			}}>
			{displayContent}
		</span>
	);

	// Tooltip shows same JSX, unclamped
	const tooltipContent = (
		<span
			className={`w-full block ${className}`}
			style={{
				lineHeight: `${lineHeight}px`,
				wordBreak: "break-word",
			}}>
			{displayContent}
		</span>
	);

	if (!isTruncated) {
		return textElement;
	}

	if (!showTooltip) {
		return textElement;
	}

	return (
		<Tooltip
			title={tooltipContent}
			placement={tooltipPlacement}
			overlayStyle={{ maxWidth: 300 }}
			overlayInnerStyle={{ maxHeight: "20rem", overflowY: "auto" }}
			getPopupContainer={getPopupContainer ?? ((triggerNode) => (triggerNode.closest(".ant-table-body") as HTMLElement) || document.body)}>
			{textElement}
		</Tooltip>
	);
};
